import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FaArrowRightLong } from "react-icons/fa6";
import { useTranslation } from "react-i18next";

const images = [
  { src: "/green-house-main.webp", titleKey: "projects_item_glasshouse", link: "/green-house" },
  { src: "/polycarbonate-main.webp", titleKey: "projects_item_polycarbonate", link: "/polycarbonate" },
  { src: "/polythene.webp", titleKey: "projects_item_polythene", link: "/polythene" },
  { src: "/screenhouse-main.webp", titleKey: "projects_item_screenhouse", link: "/screen-house" },
    { src: "/h1.webp", titleKey: "hydroponics_hero_title", link: "/hydroponics" },

];

const Projects1 = () => {
  const { t } = useTranslation();
  const [expandedImage, setExpandedImage] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isMedium, setIsMedium] = useState(false); // 1000px – 1280px

  // Width of the row, so the title can be given the width it has when the card
  // is expanded. Without that its box shrinks with the card and the text
  // re-wraps mid-animation, which reads as a flicker.
  const rowRef = useRef<HTMLDivElement>(null);
  const [rowWidth, setRowWidth] = useState(0);

  useEffect(() => {
    const checkSize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 640);
      setIsTablet(w >= 640 && w < 1000);
      setIsMedium(w >= 1000 && w < 1280);
    };
    const measure = () => {
      checkSize();
      if (rowRef.current) {
        // content box only — the row carries horizontal padding the cards sit inside
        const cs = getComputedStyle(rowRef.current);
        setRowWidth(
          rowRef.current.getBoundingClientRect().width -
            parseFloat(cs.paddingLeft) -
            parseFloat(cs.paddingRight)
        );
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Auto slide — mobile ke ilawa sab par
  useEffect(() => {
    if (isMobile) return;
    const interval = setInterval(() => {
      setExpandedImage((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [isMobile]);

  const getWidth = (index: number) => {
    if (isMobile) return "100%";
    if (isTablet) return expandedImage === index ? "50%" : "10%";
    if (isMedium) return expandedImage === index ? "48%" : "10%"; // 1000–1280px
    return expandedImage === index ? "32rem" : "10rem";            // 1280px+
  };

  // Mirrors getWidth() for the expanded state. 96px = left-4 (16) + the 80px
  // kept clear for the arrow button.
  const expandedCardPx = isMobile
    ? rowWidth
    : isTablet
      ? rowWidth * 0.5
      : isMedium
        ? rowWidth * 0.48
        : 512; // 32rem
  const titleWidth = expandedCardPx ? Math.max(120, Math.round(expandedCardPx - 96)) : undefined;

  const getHeight = () => {
    if (isMobile) return "220px";
    if (isMedium) return "22rem"; // 1000–1280px
    return "24rem";
  };

  return (
    <section
      id="projects"
      className="w-full h-auto relative scroll-mt-20 bg-white py-10 pb-16"
    >
      <h1 className="text-5xl sm:text-4xl lg:text-5xl font-extrabold text-[#002F3F] mb-12 text-left sm:text-center px-5 sm:px-0">
        {t("projects_heading")}
      </h1>

      <div className="w-full flex justify-center">
        <div ref={rowRef} className="w-full max-w-7xl px-5 sm:px-4 flex flex-col sm:flex-row gap-4 sm:gap-2 justify-center">
          {images.map((item, idx) => {
            const isActive = expandedImage === idx;

            return (
              <div
                key={idx}
                onMouseEnter={() => setExpandedImage(idx)}
                className="relative overflow-hidden rounded-3xl transition-all duration-500 cursor-pointer"
                style={{
                  width: getWidth(idx),
                  height: getHeight(),
                  flexShrink: 0,
                }}
              >
                <img
                  src={item.src}
                  alt={t(item.titleKey)}
                  loading="lazy"
                  decoding="async"
                  sizes="(max-width: 640px) 100vw, (max-width: 1000px) 55vw, (max-width: 1280px) 48vw, 42rem"
                  className="w-full h-full object-cover"
                />

                {/* Overlay, title and arrow stay mounted so they fade with the
                    width transition instead of popping in and out. The fade is
                    timed against the 500ms width change: out in 150ms (before
                    the card narrows enough for the title to re-wrap), in after a
                    200ms delay (once the card has widened). */}
                <div
                  className={`absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/10 transition-opacity ease-out ${
                    isActive || isMobile ? "opacity-100 duration-300 delay-200" : "opacity-0 duration-150"
                  }`}
                />
                <h2
                  style={{ width: titleWidth }}
                  className={`absolute bottom-4 left-4 text-white text-md sm:text-2xl lg:text-3xl font-semibold z-10 transition-opacity ease-out ${
                    isActive || isMobile ? "opacity-100 duration-300 delay-200" : "opacity-0 duration-150"
                  }`}
                  aria-hidden={!(isActive || isMobile)}
                >
                  {t(item.titleKey)}
                </h2>
                <Link
                  to={item.link}
                  aria-label={`View ${t(item.titleKey)} project`}
                  tabIndex={isActive || isMobile ? 0 : -1}
                  aria-hidden={!(isActive || isMobile)}
                  className={`transition-opacity ease-out ${
                    isActive || isMobile
                      ? "opacity-100 duration-300 delay-200"
                      : "opacity-0 duration-150 pointer-events-none"
                  }`}
                >
                  <div className="absolute bottom-4 right-4 z-10">
                    <FaArrowRightLong className="bg-white text-black p-3 rounded-full text-4xl hover:scale-110 transition" />
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Projects1;